import React, { useRef } from 'react';

// 6-digit one-time-code input. Renders six individual boxes so iOS/Android
// users get the familiar native-app feel instead of a single text field.
//
// Behavior:
// - Auto-advance focus on input
// - Backspace on an empty box jumps back and clears the previous digit
// - Arrow-left / arrow-right navigation
// - Paste from clipboard splits across boxes
// - iOS SMS one-time-code autofill (which dumps the whole code into the first
//   input) is handled by the same change handler — anything > 1 char gets
//   distributed across boxes.
// - Numbers stay LTR via dir="ltr" even when the app is in RTL mode (Arabic) —
//   numeric codes always read left-to-right regardless of script.
export function OTPInput({ value = '', onChange, autoFocus = false, length = 6 }) {
  const inputRefs = useRef([]);

  const focusAt = (idx) => {
    const el = inputRefs.current[idx];
    if (el) {
      el.focus();
      try { el.select(); } catch { /* select on number inputs throws in some browsers */ }
    }
  };

  const setAt = (i, digit) => {
    const next = (value || '').padEnd(length, ' ').split('');
    next[i] = digit;
    onChange(next.join('').replace(/ /g, ''));
  };

  const handleChange = (i, e) => {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, '');

    if (digits.length > 1) {
      // Paste-into-input or iOS one-time-code autofill — distribute across all boxes.
      const clamped = digits.slice(0, length);
      onChange(clamped);
      focusAt(Math.min(clamped.length, length - 1));
      return;
    }

    const digit = digits.slice(-1);
    if (!digit) {
      // Cleared this box
      setAt(i, ' ');
      return;
    }
    setAt(i, digit);
    if (i < length - 1) focusAt(i + 1);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      if (!value[i] && i > 0) {
        e.preventDefault();
        setAt(i - 1, ' ');
        focusAt(i - 1);
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault();
      focusAt(i + 1);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    focusAt(Math.min(pasted.length, length - 1));
  };

  return (
    <div
      className="flex gap-2 justify-center"
      onPaste={handlePaste}
      dir="ltr"
    >
      {Array.from({ length }).map((_, i) => {
        const filled = !!value[i];
        return (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={value[i] || ''}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={(e) => { try { e.target.select(); } catch { /* noop */ } }}
            autoFocus={autoFocus && i === 0}
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            aria-label={`Digit ${i + 1} of ${length}`}
            className={`
              w-11 h-14 text-center text-title-md font-bold rounded-btn border-2
              bg-white text-gray-900 transition-colors duration-150
              focus:outline-none focus:border-teal
              ${filled ? 'border-teal' : 'border-gray-200'}
            `}
          />
        );
      })}
    </div>
  );
}
